<template>
  <v-app>
    <v-main>
      <div class="sweepstakes-checkout">
        <v-container class="py-8">
          <!-- Header -->
          <v-row justify="center" class="mb-6">
            <v-col cols="12" md="8" class="text-center">
              <v-btn 
                text 
                @click="goBack"
                class="mb-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back to Entry Selection
              </v-btn>
              
              <h1 class="page-title mb-4">Complete Your Entry</h1>
              
              <countdown-timer 
                v-if="campaign && campaign.end_date"
                :end-time="campaign.end_date"
                class="mb-6"
              />
            </v-col>
          </v-row>

          <v-row justify="center">
            <!-- Order Summary -->
            <v-col cols="12" lg="4" order="2" order-lg="1">
              <v-card class="order-summary" elevation="4">
                <v-card-title class="summary-header">
                  <h3>Order Summary</h3>
                </v-card-title>
                
                <v-card-text class="pa-6">
                  <div class="summary-item">
                    <div class="item-label">Campaign:</div>
                    <div class="item-value">{{ campaign ? campaign.title : '' }}</div>
                  </div>
                  
                  <div class="summary-item">
                    <div class="item-label">Entry Tier:</div>
                    <div class="item-value">{{ tier ? tier.name : '' }}</div>
                  </div>
                  
                  <div class="summary-item">
                    <div class="item-label">Base Entries:</div>
                    <div class="item-value">{{ tier ? formatNumber(tier.entries) : '' }}</div>
                  </div>
                  
                  <div v-if="bonusEnabled && campaign && campaign.bonus_promotion" class="summary-item bonus-item">
                    <div class="item-label">Bonus Entries:</div>
                    <div class="item-value">{{ formatNumber(bonusEntries) }}</div>
                  </div>
                  
                  <v-divider class="my-4"></v-divider>
                  
                  <div class="summary-item total-item">
                    <div class="item-label">Total Entries:</div>
                    <div class="item-value total-value">{{ formatNumber(totalEntries) }}</div>
                  </div>
                  
                  <div class="summary-item total-item">
                    <div class="item-label">Total Amount:</div>
                    <div class="item-value total-value">${{ tier ? formatPrice(tier.price) : '0.00' }}</div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Bonus Entry Promotion -->
              <v-card v-if="!bonusEnabled && campaign && campaign.bonus_promotion" class="mt-4 bonus-card" elevation="4">
                <v-card-text class="pa-4">
                  <div class="bonus-offer">
                    <v-icon color="orange" size="32" class="mb-2">mdi-gift</v-icon>
                    <h4 class="bonus-title">Add {{ campaign.bonus_promotion.multiplier }}x Bonus Entries!</h4>
                    <p class="bonus-description">
                      Double your chances to win with our special bonus offer.
                    </p>
                    <v-btn
                      color="orange"
                      @click="enableBonus"
                      small
                      outlined
                    >
                      Add Bonus Entries
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Entry Form -->
            <v-col cols="12" lg="8" order="1" order-lg="2">
              <v-card class="entry-form" elevation="4">
                <v-card-title class="form-header">
                  <h3>Your Information</h3>
                </v-card-title>
                
                <v-card-text class="pa-6">
                  <v-form ref="form" v-model="formValid">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.first_name"
                          label="First Name"
                          :rules="[rules.required]"
                          outlined
                          required
                          dense
                          style="margin-bottom: -25px;"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.last_name"
                          label="Last Name"
                          :rules="[rules.required]"
                          outlined
                          required
                          dense
                          style="margin-bottom: -25px;"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.email"
                          label="Email Address"
                          :rules="[rules.required, rules.email]"
                          outlined
                          required
                          dense
                          style="margin-bottom: -25px;"
                          type="email"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.phone"
                          v-mask="'(###) ###-####'"
                          label="Phone Number"
                          placeholder="(555) 123-4567"
                          :rules="[rules.required, rules.phone]"
                          outlined
                          required
                          dense
                          style="margin-bottom: -25px;"
                          prepend-inner-icon="mdi-phone"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card-text>
              </v-card>

              <!-- Payment Section (Placeholder) -->
              <v-card class="mt-6 payment-section" elevation="4">
                <v-card-title class="form-header">
                  <h3>Payment Information</h3>
                </v-card-title>
                
                <v-card-text class="pa-6">
                  <v-alert
                    type="info"
                    prominent
                    border="left"
                    colored-border
                  >
                    <div class="payment-placeholder">
                      <v-icon size="48" color="info" class="mb-2">mdi-credit-card-outline</v-icon>
                      <h4>Payment Integration Coming Soon</h4>
                      <p class="mb-0">
                        Payment processing will be integrated in the next phase. 
                        For now, you can complete your entry and we'll contact you for payment.
                      </p>
                    </div>
                  </v-alert>
                </v-card-text>
              </v-card>

              <!-- Submit Button -->
              <div class="text-center mt-6">
                <v-btn
                  x-large
                  color="orange"
                  :disabled="!formValid || submitting"
                  :loading="submitting"
                  @click="submitEntry"
                  class="submit-btn"
                  dark
                >
                  Complete Entry
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <!-- Loading State -->
          <v-overlay :value="loading">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>
        </v-container>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import CountdownTimer from './CountdownTimer.vue'
import { mask } from 'v-mask'

export default {
  name: 'SweepstakesCheckout',
  directives: {
    mask
  },
  components: {
    CountdownTimer
  },
  props: {
    campaignId: {
      type: [String, Number],
      required: true
    },
    tierId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      submitting: false,
      formValid: false,
      campaign: null,
      tier: null,
      bonusEnabled: false,
      referralCode: null, // Store referral code
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      },
      rules: {
        required: value => !!value || 'This field is required',
        email: value => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return pattern.test(value) || 'Please enter a valid email address'
        },
        phone: value => {
          // Remove mask characters and validate
          const cleanPhone = value ? value.replace(/[\s\-\(\)]/g, '') : ''
          const pattern = /^\d{10}$/
          return pattern.test(cleanPhone) || 'Please enter a valid 10-digit phone number'
        }
      }
    }
  },
  computed: {
    bonusEntries() {
      if (!this.bonusEnabled || !this.tier || !this.campaign || !this.campaign.bonus_promotion) {
        return 0
      }
      const multiplier = this.campaign.bonus_promotion.multiplier || 2
      return this.tier.entries * (multiplier - 1)
    },
    
    totalEntries() {
      if (!this.tier) return 0
      return this.tier.entries + this.bonusEntries
    }
  },
  async mounted() {
    this.bonusEnabled = this.$route.query.bonus === '1'

    // Capture referral code from URL or localStorage
    this.referralCode = this.$route.query.ref || localStorage.getItem('sweepstakes_referral_code') || null
    if (this.referralCode) {
      console.log('Referral code for checkout:', this.referralCode)
    }

    // Load stored user info if available
    if (this.$route.query.userInfo === 'stored') {
      const storedInfo = JSON.parse(localStorage.getItem('sweepstakes_user_info') || '{}')
      if (storedInfo.first_name) {
        this.form = { ...this.form, ...storedInfo }
      }
    }

    await this.loadData()
  },
  methods: {
    async loadData() {
      try {
        this.loading = true
        
        // Load campaign
        const campaignResponse = await window.axios.get(`api/sweepstakes/campaign/${this.campaignId}`)
        if (campaignResponse.data.flag === 1) {
          this.campaign = campaignResponse.data.campaign
        }

        // Load tier
        const tiersResponse = await window.axios.get(`api/sweepstakes/tiers/${this.campaignId}`)
        if (tiersResponse.data.flag === 1) {
          this.tier = tiersResponse.data.tiers.find(t => t.id == this.tierId)
        }
        
        if (!this.campaign || !this.tier) {
          throw new Error('Campaign or tier not found')
        }
        
      } catch (error) {
        console.error('Error loading data:', error)
        this.$toast.error('Failed to load sweepstakes information')
        this.$router.push('/sweepstakes')
      } finally {
        this.loading = false
      }
    },
    
    enableBonus() {
      this.bonusEnabled = true
    },
    
    async submitEntry() {
      if (!this.$refs.form.validate()) {
        return
      }
      
      try {
        this.submitting = true
        
        const entryData = {
          campaign_id: this.campaignId,
          tier_id: this.tierId,
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          email: this.form.email,
          phone: this.form.phone,
          bonus_entries: this.bonusEnabled,
          referral_code: this.referralCode // Include referral code
        }
        
        const response = await window.axios.post('api/sweepstakes/entry', entryData)
        
        if (response.data.flag === 1) {
          this.$toast.success('Entry submitted successfully!')
          this.$router.push({
            name: 'SweepstakesConfirmation',
            params: { entryId: response.data.entry.id }
          })
        } else {
          throw new Error(response.data.message || 'Failed to submit entry')
        }
        
      } catch (error) {
        console.error('Error submitting entry:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to submit entry')
      } finally {
        this.submitting = false
      }
    },
    
    goBack() {
      this.$router.push({
        name: 'SweepstakesEntry',
        params: { campaignId: this.campaignId }
      })
    },
    
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    
    formatNumber(value) {
      return new Intl.NumberFormat('en-US').format(value)
    }
  }
}
</script>

<style scoped>
.sweepstakes-checkout {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.page-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #333;
}

.order-summary,
.entry-form,
.payment-section {
  border-radius: 5px;
}

.summary-header,
.form-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 24px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.item-label {
  color: #666;
  font-weight: 500;
}

.item-value {
  font-weight: bold;
  color: #333;
}

.bonus-item .item-value {
  color: #FF9800;
}

.total-item {
  font-size: 1.1rem;
}

.total-value {
  color: #4CAF50 !important;
  font-size: 1.2rem;
}

.bonus-card {
  border: 2px solid #FF9800;
  border-radius: 12px;
}

.bonus-offer {
  text-align: center;
}

.bonus-title {
  color: #FF9800;
  margin-bottom: 8px;
}

.bonus-description {
  color: #666;
  margin-bottom: 16px;
}

.payment-placeholder {
  text-align: center;
}

.submit-btn {
  font-size: 1.1rem;
  padding: 16px 48px !important;
  border-radius: 50px;
  text-transform: none;
  font-weight: bold;
}

/* Mobile Input Enhancements */
.font-size-input input {
  font-size: 16px !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.input-style .v-input__control .v-input__slot {
  min-height: 48px !important;
}

.input-style .v-text-field__details {
  padding-top: 4px;
}

/* Phone Input Specific Styling */
.input-style input[type="tel"] {
  letter-spacing: 1px;
}

/* Prevent zoom on iOS */
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .input-style input,
  .input-style textarea,
  .input-style select {
    font-size: 16px !important;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
}
</style>
